import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function CaseTestimonial() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-950 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />

      {/* Background image blur */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20bicycle%20store%20interior%20with%20sleek%20product%20displays%20premium%20road%20bikes%20mountain%20bikes%20on%20wall%20racks%2C%20warm%20ambient%20lighting%2C%20upscale%20retail%20atmosphere%2C%20blurred%20background%20photography%2C%20bokeh%20effect%2C%20editorial&width=1400&height=700&seq=cs-store1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center opacity-8"
        />
        <div className="absolute inset-0 bg-zinc-950/92" />
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Quote icon */}
          <div className="flex justify-center mb-10">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-500/10 border border-orange-500/20 rounded-2xl">
              <i className="ri-double-quotes-l text-3xl text-orange-400" />
            </div>
          </div>

          {/* Stars */}
          <div className="flex justify-center gap-1.5 mb-8">
            {[1, 2, 3, 4, 5].map((s) => (
              <i key={s} className="ri-star-fill text-orange-400 text-xl" />
            ))}
          </div>

          {/* Quote */}
          <blockquote className="text-center text-2xl md:text-3xl text-white font-bold leading-relaxed mb-10">
            „Boostio nám zmenilo pohľad na digitálny marketing. Čakal som výsledky po roku -
            prvé merateľné zlepšenia sme videli{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              do 6 týždňov od spustenia
            </span>
            . Dnes je náš e-shop produktívnejší ako kamenná predajňa. Oľutujem len jedno -
            že sme nezačali skôr."
          </blockquote>

          {/* Metrics row */}
          <div className="flex flex-wrap justify-center gap-6 mb-10">
            {[
              { label: 'Rast tržieb', value: '+312%' },
              { label: 'ROI kampaní', value: '820%' },
              { label: 'Mesiace spolupráce', value: '12' },
            ].map((m) => (
              <div key={m.label} className="text-center">
                <p className="text-2xl font-black text-orange-400 leading-none mb-1">{m.value}</p>
                <p className="text-zinc-500 text-xs">{m.label}</p>
              </div>
            ))}
          </div>

          {/* Author */}
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-orange-500/30">
              <img
                src="https://readdy.ai/api/search-image?query=middle%20aged%20slovak%20businessman%20sporty%20casual%20look%2C%20clean%20white%20background%20headshot%20portrait%2C%20confident%20friendly%20smile&width=120&height=120&seq=cs-client2&orientation=squarish"
                alt="Tomáš Selecký"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="text-center">
              <p className="text-white font-bold text-base">Tomáš Selecký</p>
              <p className="text-zinc-500 text-sm">Majiteľ & CEO · SalenBike s.r.o.</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-zinc-600 text-xs" />
                </div>
                <span className="text-zinc-600 text-xs">Bratislava, Slovensko</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
